import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import MultiSearchableSelect, {
  Option,
} from "../../core/common/MultiSearchableSelect";
import CustomerScoreCard from "./tabs-component/CustomerScoreCard";
import ColorCode from "./tabs-component/ColorCode";
import { Chart } from "primereact/chart";
import Footer from "../../core/common/Footer"; 
import "./dashboard.scss";
import useApi from "../../core/customhook/useApi";
import { useAuth } from "../../core/context/useAuthContext";
import webservice_url from "../../core/webservice/Webservice_url";

const BRAND_BLUE = "#34359B";
const BRAND_BLUE_SOFT = "#6667C2";
const BRAND_ORANGE = "#FF7A00"; 
const ACCENT_RED = "#F54B5E";

interface CountData {
  key: string;
  status_id: string;
  label: string;
  count: number;
}

interface FilterState {
  program: string[];
  sbu: string[];
}

const tabs = [
  {
    id: "customerScoreCard",
    label: "Client Scorecard",
  },
];

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id);
  const [inviteData, setInviteData] = useState<FilterState>({
    program: [],
    sbu: [],
  });
  const [caseData, setCaseData] = useState<FilterState>({
    program: [],
    sbu: [],
  });
  const [slaData, setSlaData] = useState<FilterState>({
    program: [],
    sbu: [],
  });
 
  const [areaData, setAreaData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: "SLA %",
        data: [],
        fill: true,
        borderColor: BRAND_ORANGE,
        backgroundColor: "rgba(255, 122, 0, 0.12)",
        tension: 0.4,
      },
    ],
  });
 
  const areaChartOption = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#34359B",
        titleColor: "#fff",
        bodyColor: "#fff",
        displayColors: false,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => `${context.raw}%`,
        },
      },
    },

    interaction: {
      intersect: false,
      mode: "index",
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#6b7280",
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: (value: any) => value + "%",
          color: "#6b7280",
        },
        grid: {
          color: "#ececec",
        },
      },
    },

    elements: {
      line: {
        tension: 0.45,
        borderWidth: 4,
      },
      point: {
        radius: 6,
        hoverRadius: 9,
        borderWidth: 3,
        backgroundColor: "#FF7A00",
        borderColor: "#fff",
      },
    },
  };

  const getCount = (key: string) =>
    data.find((item) => item.key === key)?.count ?? 0;

  const { auth } = useAuth();
  const [projects, setProjects] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const orgId = auth?.orgId;
  const [data, setData] = useState<CountData[]>([]);

  const [inviteProgramOptions, setInviteProgramOptions] = useState<Option[]>(
    [],
  );
  const [caseProgramOptions, setCaseProgramOptions] = useState<Option[]>([]);
  const [slaProgramOptions, setSlaProgramOptions] = useState<Option[]>([]);

  const handleSbuMultiSelectChange = (
    setter: Dispatch<SetStateAction<FilterState>>,
    values: string[],
  ) => {
    setter((prev: FilterState) => ({ ...prev, sbu: values }));
  };

  const handleProjectMultiSelectChange = (
    setter: Dispatch<SetStateAction<FilterState>>,
    values: string[],
  ) => {
    setter((prev: FilterState) => ({ ...prev, program: values, sbu: [] }));
  };

  const getProjects = useApi<any>(
    `/api/v1/master/organizations/${orgId}/projects`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const inviteProjectId = inviteData.program[0] || "";
  const caseProjectId = caseData.program[0] || "";
  const slaProjectId = slaData.program[0] || "";

  const getInviteProgram = useApi<any>(
    inviteProjectId
      ? `/api/v1/master/projects/${inviteProjectId}/programs`
      : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getMonthsReport = useApi<any>(webservice_url.dashboardMonthsReportAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const getCaseProgram = useApi<any>(
    caseProjectId ? `/api/v1/master/projects/${caseProjectId}/programs` : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getSlaProgram = useApi<any>(
    slaProjectId ? `/api/v1/master/projects/${slaProjectId}/programs` : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getTotalCounts = useApi<any>(webservice_url.getTotalCounts, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const [caseStatusData, setCaseStatusData] = useState<CountData[]>([]);

  const getCaseStatusCount = (key: string) =>
    caseStatusData.find((item) => item.key === key)?.count ?? 0;

  const OtherCaseStatusCount = useApi<any>(
    webservice_url.getTotalCountsGlobal,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const fetchGetData = async () => {
    try {
      const res = await OtherCaseStatusCount.callApi({
        project_ids: caseData.program,
        program_ids: caseData.sbu,
      }); 
      setCaseStatusData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setCaseStatusData([]);
    }
  };

  const wipCount = getCaseStatusCount("WORK_IN_PROGRESS");
  const stoppedCount = getCaseStatusCount("STOP");
  const completedCount =
    getCaseStatusCount("FULL_REPORT_GEN") +
    getCaseStatusCount("INTERIM_REPORT_GEN"); 
  const fetchMonthsReport = async () => {
    try {
      const res = await getMonthsReport.callApi({
        project_ids: slaData.program,
        program_ids: slaData.sbu,
      });

      if (res?.success) {
        const chartData = res.data || [];

        const labels = chartData.map((item: any) => {
          const [year, month] = item.month.split("-");

          return new Date(Number(year), Number(month) - 1).toLocaleString(
            "default",
            {
              month: "short",
            },
          );
        });

        const data = chartData.map((item: any) => item.sla_percentage);

        setAreaData({
          labels: labels,
          datasets: [
            {
              label: "SLA %",
              data: data,
              fill: true,
              borderColor: "#FF7A00",
              backgroundColor: (context: any) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;

                if (!chartArea) return null;

                const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom,
                );

                gradient.addColorStop(0, "rgba(255,122,0,0.35)");
                gradient.addColorStop(1, "rgba(255,122,0,0)");

                return gradient;
              },
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBorderWidth: 3,
              pointBackgroundColor: "#FF7A00",
              pointBorderColor: "#fff",
              tension: 0.4,
            },
          ],
        });
      } else {
        setAreaData({
          labels: [],
          datasets: [
            {
              label: "SLA %",
              data: [],
              fill: true,
              borderColor: BRAND_ORANGE,
              backgroundColor: "rgba(255, 122, 0, 0.12)",
              tension: 0.4,
            },
          ],
        });
      }
    } catch (err) {
      console.error(err);

      setAreaData({
        labels: [],
        datasets: [
          {
            label: "SLA %",
            data: [],
            fill: true,
            borderColor: BRAND_ORANGE,
            backgroundColor: "rgba(255, 122, 0, 0.12)",
            tension: 0.4,
          },
        ],
      });
    }
  }; 
  useEffect(() => {
    fetchMonthsReport();
  }, [slaData.program, slaData.sbu]);
  const casesData = [
    {
      label: "WIP",
      value: wipCount,
      colorCode: BRAND_ORANGE,
    },
    {
      label: "Stopped",
      value: stoppedCount,
      colorCode: ACCENT_RED,
    },
    {
      label: "Completed",
      value: completedCount,
      colorCode: BRAND_BLUE,
    }, 
  ];

  const totalCaseCount = casesData.reduce((sum, item) => sum + item.value, 0);

  const fetchInvitePrograms = async () => {
    if (!inviteProjectId) {
      setInviteProgramOptions([]);
      return;
    }

    try {
      const res = await getInviteProgram.callApi();

      if (res?.success) {
        const programOptions = (res.data || []).map((item: any) => ({
          value: item.program_id,
          label: item.program_name,
        }));

        setInviteProgramOptions(programOptions);
      } else {
        setInviteProgramOptions([]);
      }
    } catch (error) {
      console.error(error);
      setInviteProgramOptions([]);
    }
  };

  const fetchCasePrograms = async () => {
    if (!caseProjectId) {
      setCaseProgramOptions([]);
      return;
    }

    try {
      const res = await getCaseProgram.callApi();

      if (res?.success) {
        const programOptions = (res.data || []).map((item: any) => ({
          value: item.program_id,
          label: item.program_name,
        }));

        setCaseProgramOptions(programOptions);
      } else {
        setCaseProgramOptions([]);
      }
    } catch (error) {
      console.error(error);
      setCaseProgramOptions([]);
    }
  };

  const fetchSlaPrograms = async () => {
    if (!slaProjectId) {
      setSlaProgramOptions([]);
      return;
    }

    try {
      const res = await getSlaProgram.callApi();

      if (res?.success) {
        const programOptions = (res.data || []).map((item: any) => ({
          value: item.program_id,
          label: item.program_name,
        }));

        setSlaProgramOptions(programOptions);
      } else {
        setSlaProgramOptions([]);
      }
    } catch (error) {
      console.error(error);
      setSlaProgramOptions([]);
    }
  };

  const fetchData = async () => {
    try {
      const res = await getTotalCounts.callApi({
        project_ids: inviteData.program,
        program_ids: inviteData.sbu,
      }); 
      setData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setData([]);
    }
  };

  const plansData = [
    {
      label: "Pending Login",
      value: getCount("PENDING_LOGIN"),
      colorCode: BRAND_ORANGE,
    },
    {
      label: "Invited Expired",
      value: getCount("EXPIRED_INVITATION"),
      colorCode: ACCENT_RED,
    },
    {
      label: "Applicant Logged-in",
      value: getCount("LOGIN"),
      colorCode: BRAND_BLUE_SOFT,
    },
    {
      label: "In Progress",
      value: getCount("PENDING_SUBMISSION"),
      colorCode: BRAND_BLUE,
    },
    {
      label: "Application Submitted",
      value: getCount("APPLICATION_SUBMITTED"),
      colorCode: "#22C55E",
    },
  ];

  const totalInviteCount = plansData.reduce(
    (sum, item) => sum + (item.value || 0),
    0,
  );
  const loadProjects = async () => {
    if (!orgId) return;

    setLoading(true);

    try {
      const res = await getProjects.callApi();

      if (res?.success) {
        const projectOptions = (res.data || []).map((item: any) => ({
          value: item.project_id,
          label: item.project_name,
        }));

        setProjects(projectOptions);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [inviteData.program, inviteData.sbu]);

  useEffect(() => {
    fetchGetData();
  }, [caseData.program, caseData.sbu]);

  useEffect(() => {
    fetchInvitePrograms();
  }, [inviteProjectId]);

  useEffect(() => {
    fetchCasePrograms();
  }, [caseProjectId]);

  useEffect(() => {
    fetchSlaPrograms();
  }, [slaProjectId]);

  const inviteChartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "donut",
    },

    labels: plansData.map((item) => item.label),

    colors: plansData.map((item) => item.colorCode),

    legend: {
      show: false,
    },

    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      pie: {
        donut: {
          size: "78%",
          labels: {
            show: true,

            total: {
              show: true,
              label: "Total",

              formatter: () => totalInviteCount.toString(),
            },
          },
        },
      },
    },

    tooltip: {
      y: {
        formatter: (value) => value.toString(),
      },
    },
  };

  const caseChartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "donut",
    },

    labels: casesData.map((item) => item.label),

    colors: casesData.map((item) => item.colorCode),

    legend: {
      show: false,
    },

    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      pie: {
        donut: {
          size: "78%",

          labels: {
            show: true,

            total: {
              show: true,

              label: "Total",

              formatter: () => totalCaseCount.toString(),
            },
          },
        },
      },
    },

    tooltip: {
      y: {
        formatter: (value) => value.toString(),
      },
    },
  };
 
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setChartKey((prev) => prev + 1);
      window.dispatchEvent(new Event("resize"));
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="page-wrapper auptech-dashboard">
      <div className="content p-2 p-md-3"> 
        <div className="db-hero">
          <div className="db-hero__glow" aria-hidden="true" />
          <div className="db-hero__content">
            <p className="db-hero__eyebrow">Overview</p>
            <h1 className="db-hero__title">Dashboard</h1>
            <p className="db-hero__subtitle">
              Today&rsquo;s overview of your organization
            </p>
          </div>
          <div className="db-hero__stats">
            <div className="db-hero__stat">
              <span className="db-hero__stat-value">{totalInviteCount}</span>
              <span className="db-hero__stat-label">Total Invited</span>
            </div>
            <div className="db-hero__stat">
              <span className="db-hero__stat-value">{totalCaseCount}</span>
              <span className="db-hero__stat-label">Total Cases</span>
            </div>
          </div>
        </div>
        {/* /Hero overview panel */}

        <div className="row mx- gx-3 mt-4">
          {/* Invite Progress */}
          <div className="col-12 col-lg-6 col-xl-4 d-flex">
            <div className="stat-card flex-fill">
              <div className="stat-card__decor" aria-hidden="true" />
              <div className="stat-card__top">
                <div className="stat-card__icon-ring stat-card__icon-ring--orange">
                  <i className="ti ti-user-check" />
                </div>
                <div className="stat-card__headline">
                  <span className="stat-card__value">{totalInviteCount}</span>
                  <span className="stat-card__label">Total Invited</span>
                </div>
              </div>

              <div className="stat-card__title-row">
                <div>
                  <h5 className="stat-card__title">Invite Status</h5>
                  <p className="stat-card__subtitle">
                    Applicant invitation funnel
                  </p>
                </div>
              </div>

              <div className="stat-card__filters row mb-2 gx-2">
                <div className="col-6 db-select-group">
                  <MultiSearchableSelect
                    label="Program Assignment"
                    name="program"
                    value={inviteData.program}
                    options={projects}
                    loading={loading}
                    onChange={(_name, values) =>
                      handleProjectMultiSelectChange(setInviteData, values)
                    }
                  />
                </div>
                <div className="col-6 db-select-group">
                  <MultiSearchableSelect
                    label="Business Unit"
                    name="sbu"
                    value={inviteData.sbu}
                    options={inviteProgramOptions}
                    onChange={(_name, values) =>
                      handleSbuMultiSelectChange(setInviteData, values)
                    }
                  />
                </div>
              </div>

              <div className="stat-card__chart mt-3">
                <div className="text-center"> 
                  <ReactApexChart
                    key={JSON.stringify(plansData)}
                    type="donut"
                    height={200}
                    width={200}
                    series={plansData.map((item) => item.value)}
                    options={inviteChartOptions}
                  />
                </div>

                <div className="donut-legend">
                  {plansData.map((item, index) => (
                    <div key={index} className="donut-legend__item">
                      <span
                        className="dot"
                        style={{ background: item.colorCode }}
                      />
                      {item.label} - <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6 col-xl-4 d-flex">
            <div className="stat-card stat-card--accent-blue flex-fill">
              <div className="stat-card__decor" aria-hidden="true" />
              <div className="stat-card__top">
                <div className="stat-card__icon-ring stat-card__icon-ring--blue">
                  <i className="ti ti-briefcase" />
                </div>
                <div className="stat-card__headline">
                  <span className="stat-card__value">{totalCaseCount}</span>
                  <span className="stat-card__label">Total Cases</span>
                </div>
              </div>

              <div className="stat-card__title-row">
                <div>
                  <h5 className="stat-card__title">Case Status Overview</h5>
                  <p className="stat-card__subtitle">
                    Where every case stands right now
                  </p>
                </div>
              </div>

              <div className="stat-card__filters row mb-2 gx-2">
                <div className="col-6 db-select-group">
                  <MultiSearchableSelect
                    label="Program Assignment"
                    name="program"
                    value={caseData.program}
                    options={projects}
                    loading={loading}
                    onChange={(_name, values) =>
                      handleProjectMultiSelectChange(setCaseData, values)
                    }
                  />
                </div>
                <div className="col-6 db-select-group">
                  <MultiSearchableSelect
                    label="Business Unit"
                    name="sbu"
                    value={caseData.sbu}
                    options={caseProgramOptions}
                    onChange={(_name, values) =>
                      handleSbuMultiSelectChange(setCaseData, values)
                    }
                  />
                </div>
              </div>

              <div className="stat-card__chart mt-3">
                <div className="text-center"> 
                  <ReactApexChart
                    key={JSON.stringify(casesData)}
                    type="donut"
                    height={200}
                    width={200}
                    series={casesData.map((item) => item.value)}
                    options={caseChartOptions}
                  />
                </div>
                <div className="donut-legend">
                  {casesData.map((item, index) => (
                    <div key={index} className="donut-legend__item">
                      <span
                        className="dot"
                        style={{ background: item.colorCode }}
                      />
                      {item.label} - <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-xl-4 d-flex">
            <div className="stat-card stat-card--accent-blue flex-fill">
              <div className="stat-card__decor" aria-hidden="true" />
              <div className="stat-card__title-row">
                <div className="stat-card__icon-ring stat-card__icon-ring--blue">
                  <i className="ti ti-gauge" />
                </div>
                <div>
                  <h5 className="stat-card__title">SLA Performance</h5>
                  <p className="stat-card__subtitle">
                    Trend across the reporting period
                  </p>
                </div>
              </div>

              <div className="stat-card__filters row mb-2 gx-2">
                <div className="col-6 col-sm-4 col-lg-6 db-select-group">
                  <MultiSearchableSelect
                    label="Program Assignment"
                    name="program"
                    value={slaData.program}
                    options={projects}
                    loading={loading}
                    onChange={(_name, values) =>
                      handleProjectMultiSelectChange(setSlaData, values)
                    }
                  />
                </div>
                <div className="col-6 col-sm-4 col-lg-6 db-select-group">
                  <MultiSearchableSelect
                    label="Business Unit"
                    name="sbu"
                    value={slaData.sbu}
                    options={slaProgramOptions}
                    onChange={(_name, values) =>
                      handleSbuMultiSelectChange(setSlaData, values)
                    }
                  />
                </div>
              </div> 
              <div className="stat-card__linechart mt-4 text-center">
                <Chart type="line" data={areaData} options={areaChartOption} />
              </div>
            </div>
          </div>
        </div>
        <div className="db-section-head">
          <div>
            <p className="db-section-eyebrow">Detailed Reports</p>
            <h2 className="db-section-title">Reports &amp; Breakdown</h2>
          </div>

          <div className="db-segmented">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`db-segmented__btn ${activeTab === tab.id ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="db-tab-panel">
          {activeTab == "customerScoreCard" && <CustomerScoreCard />}
          {activeTab == "colorCode" && <ColorCode />}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard; 